<template>
    <div class="secure-sign__container">
        <v-container>
            <s-s-form-header
                title="Denzapine Monitoring Service Patient Registration Form"
                document-number="PV01F05"
                version="8.0"
                effective-date="31 Jan 2024"
                review-date="31 Jan 2026"
                supersedes="7.0"
            ></s-s-form-header>
            <v-col class="text-center">
                <h1 class="mb-2">Denzapine Monitoring Service Patient Registration Form</h1>
                <h4>
                    Please complete all form fields before submission of application. Missing information may result in
                    delays to patient registration.
                </h4>
                <h3 class="mb-8">THE PATIENT CANNOT BE REGISTERED WITHOUT A VALID BLOOD RESULT</h3>
            </v-col>

            <v-col cols="12">
                <v-form ref="form" v-model="isFormValid" :disabled="isDisabled">
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientUseType"
                                label="This patient will be on:"
                                mandatory
                                :rules="validationRules.patientUseType"
                                :radio-data="[
                                    { label: 'Tablets', value: 'Tablets' },
                                    { label: 'Suspension', value: 'Suspension' },
                                ]"
                            ></secure-sign-form-radio-group>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::patientRegistrationForm.proposedDateOfRegistration"
                                label="Proposed Date of Registration:"
                                :rules="validationRules.proposedDateOfRegistration"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Patient Details</h5>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientFirstName"
                                :rules="validationRules.patientFirstName"
                                label="Patient First Name:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientLastName"
                                label="Patient Last Name:"
                                :rules="validationRules.patientLastName"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::patientRegistrationForm.dateOfBirth"
                                label="Date of Birth:"
                                :rules="validationRules.dateOfBirth"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.race"
                                label="Race:"
                                mandatory
                                :rules="validationRules.race"
                                :radio-data="[
                                    { label: 'Caucasian', value: 'Caucasian' },
                                    { label: 'Asian', value: 'Asian' },
                                    { label: 'Afro-Caribbean', value: 'Afro-Caribbean' },
                                    { label: 'Mixed', value: 'Mixed' },
                                    { label: 'Other, please specify', value: 'Other' },
                                ]"
                                connected-field="patientRegistrationForm.otherRace"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.otherRace"
                                :rules="isRaceAdditionalFieldDisabled ? validationRules.otherRace : []"
                                label="Other, please specify"
                                :mandatory="isRaceAdditionalFieldDisabled"
                                :disabled="!isRaceAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.gender"
                                label="Gender:"
                                mandatory
                                :rules="validationRules.gender"
                                :radio-data="[
                                    { label: 'Male', value: 'Male' },
                                    { label: 'Female', value: 'Female' },
                                    { label: 'Other', value: 'Other' },
                                ]"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.history"
                                label="History:"
                                mandatory
                                :rules="validationRules.history"
                                :radio-data="[
                                    {
                                        label: 'New Patient (never taken Clozapine before)',
                                        value: 'New Patient (never taken Clozapine before)',
                                    },
                                    { label: 'Restart (previous brand)', value: 'Restart (previous brand)' },
                                ]"
                                connected-field="patientRegistrationForm.previousBrand"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.previousBrand"
                                :rules="isPreviousBrandAdditionalFieldDisabled ? validationRules.previousBrand : []"
                                label="Previous brand:"
                                :mandatory="isPreviousBrandAdditionalFieldDisabled"
                                :disabled="!isPreviousBrandAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Pharmacy Details</h5>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.dispensingPharmacy"
                                :rules="validationRules.dispensingPharmacy"
                                label="Dispensing Pharmacy:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.pharmacyPhoneNumber"
                                :rules="validationRules.pharmacyPhoneNumber"
                                label="Telephone Number:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.pharmacyEmail"
                                :rules="validationRules.pharmacyEmail"
                                label="Email Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.pharmacyAddress"
                                :rules="validationRules.pharmacyAddress"
                                label="Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Prescriber Details</h5>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.nameOfPrescriber"
                                :rules="validationRules.nameOfPrescriber"
                                label="Name:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.gmcNumber"
                                :rules="validationRules.gmcNumber"
                                label="GMC Number:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.prescriberEmailAddress"
                                :rules="validationRules.prescriberEmailAddress"
                                label="Email Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Ward Details</h5>
                        </v-col>
                        <v-col cols="12" sm="12" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientType"
                                label=""
                                mandatory
                                :rules="validationRules.patientType"
                                :radio-data="[
                                    { label: 'Inpatient', value: 'Inpatient' },
                                    { label: 'Outpatient', value: 'Outpatient' },
                                ]"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                connected-field="patientRegistrationForm.patientType"
                                :disabled="isPatientTypeOutpatient"
                                to-mutate="secure_sign_forms::patientRegistrationForm.wardAddress"
                                :rules="validationRules.wardAddress"
                                label="Ward Address:"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4"></v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                connected-field="patientRegistrationForm.patientType"
                                :disabled="isPatientTypeOutpatient"
                                to-mutate="secure_sign_forms::patientRegistrationForm.wardContactName"
                                :rules="validationRules.wardContactName"
                                label="Contact Name(s):"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                connected-field="patientRegistrationForm.patientType"
                                :disabled="isPatientTypeOutpatient"
                                to-mutate="secure_sign_forms::patientRegistrationForm.wardTelephoneNumber"
                                :rules="validationRules.wardTelephoneNumber"
                                label="Telephone Number:"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                connected-field="patientRegistrationForm.patientType"
                                :disabled="isPatientTypeOutpatient"
                                to-mutate="secure_sign_forms::patientRegistrationForm.wardEmail"
                                :rules="validationRules.wardEmail"
                                label="Email:"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">
                                Clozapine clinic/blood sampling/collection location (Where the blood is to be taken)
                            </h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.nameOfSite"
                                :rules="validationRules.nameOfSite"
                                label="Name of site:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.clinicAddress"
                                :rules="validationRules.clinicAddress"
                                label="Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.clinicContactName"
                                :rules="validationRules.clinicContactName"
                                label="Contact Name(s):"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.clinicPhoneNumber"
                                :rules="validationRules.clinicPhoneNumber"
                                label="Telephone Number:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.clinicEmailAddress"
                                :rules="validationRules.clinicEmailAddress"
                                label="Email Address:"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Diagnosis</h5>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.neutropenia"
                                label="Has the patient ever had an episode of neutropenia?"
                                mandatory
                                :rules="validationRules.neutropenia"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.redStatus"
                                label="Does the patient have a confirmed red status in their history?"
                                mandatory
                                :rules="validationRules.redStatus"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.beningEthnicNeutropenia"
                                label="Does the patient have Benign Ethnic Neutropenia (BEN)?"
                                mandatory
                                :rules="validationRules.beningEthnicNeutropenia"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.treatmentResistantSchizophrenia"
                                label="Does the patient have Treatment Resistant Schizophrenia?"
                                mandatory
                                :rules="validationRules.treatmentResistantSchizophrenia"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.parkinsonDisease"
                                label="Does this patient have Psychosis in Parkinson’s Disease?"
                                mandatory
                                :rules="validationRules.parkinsonDisease"
                                :radio-data="radioDataBinary"
                                connected-field="patientRegistrationForm.diagnosisDetails"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.diagnosisDetails"
                                :rules="
                                    isParkinsonDiseaseAdditionalFieldDisabled ? validationRules.diagnosisDetails : []
                                "
                                label="If no, please specify Diagnosis (an off-label (PV01F12) agreement may be required):"
                                :mandatory="isParkinsonDiseaseAdditionalFieldDisabled"
                                :disabled="!isParkinsonDiseaseAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.contraindicationsToClozapine"
                                label="Are there any contraindications to clozapine in the patient’s history?"
                                mandatory
                                :rules="validationRules.contraindicationsToClozapine"
                                :radio-data="radioDataBinary"
                                connected-field="patientRegistrationForm.contraindicationsToClozapineDetails"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.contraindicationsToClozapineDetails"
                                :rules="
                                    isContraindicationsToClozapineAdditionalFieldDisabled
                                        ? validationRules.contraindicationsToClozapineDetails
                                        : []
                                "
                                label="If yes, please provide details (an off-label agreement may be required):"
                                :mandatory="isContraindicationsToClozapineAdditionalFieldDisabled"
                                :disabled="!isContraindicationsToClozapineAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.boneMarrowFunction"
                                label="Has the patient had impaired bone marrow function?"
                                mandatory
                                :rules="validationRules.boneMarrowFunction"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientOutsideOfUK"
                                label="Is the patient transferring from outside of the UK?"
                                mandatory
                                :rules="validationRules.patientOutsideOfUK"
                                :radio-data="radioDataBinary"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.antipsychotics"
                                label="Has the patient taken any other antipsychotics?"
                                mandatory
                                :rules="validationRules.antipsychotics"
                                :radio-data="radioDataBinary"
                                connected-field="patientRegistrationForm.specifyAntipsychotics"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.specifyAntipsychotics"
                                :rules="
                                    isAntipsychoticsAdditionalFieldDisabled ? validationRules.specifyAntipsychotics : []
                                "
                                label="If yes, please specify:"
                                :mandatory="isAntipsychoticsAdditionalFieldDisabled"
                                :disabled="!isAntipsychoticsAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Baseline Blood Results</h5>
                            <p class="mt-5 font-weight-bold">
                                NB: The blood sample must have been taken within 10 days of the date of treatment
                                initiation, and ideally less than 7 days
                            </p>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                to-mutate="secure_sign_forms::patientRegistrationForm.dateOfBaselineBloodTest"
                                label="Date of Baseline Blood Test:"
                                :rules="validationRules.dateOfBaselineBloodTest"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.whiteBloodCellCount"
                                :rules="validationRules.whiteBloodCellCount"
                                label="White Blood Cell Count (X 10<sup>9</sup>/L):"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.neutrophilCount"
                                :rules="validationRules.neutrophilCount"
                                label="Neutrophil Count (X 10<sup>9</sup>/L):"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.plateletCount"
                                :rules="validationRules.plateletCount"
                                label="Platelet Count (X 10<sup>9</sup>/L):"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="8" class="py-0">
                            <secure-sign-form-radio-group
                                to-mutate="secure_sign_forms::patientRegistrationForm.patientBarcodeLabels"
                                label="Patient barcode Labels to be sent to:"
                                mandatory
                                :rules="validationRules.patientBarcodeLabels"
                                :radio-data="[
                                    { label: 'Dispensing Pharmacy', value: 'Dispensing Pharmacy' },
                                    {
                                        label: 'Blood sampling / Collection Centre',
                                        value: 'Blood sampling / Collection Centre',
                                    },
                                    { label: 'Other', value: 'Other' },
                                ]"
                                connected-field="patientRegistrationForm.otherBarcodeLabels"
                            ></secure-sign-form-radio-group>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.otherBarcodeLabels"
                                :rules="isBarcodesAdditionalFieldDisabled ? validationRules.otherBarcodeLabels : []"
                                label="Please specify:"
                                :mandatory="isBarcodesAdditionalFieldDisabled"
                                :disabled="!isBarcodesAdditionalFieldDisabled"
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Additional Information</h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <b-textarea
                                :auto-grow="true"
                                :printable="true"
                                to-mutate="secure_sign_forms::patientRegistrationForm.additionalInformation"
                                label=""
                                :rules="validationRules.additionalInformation"
                                mandotory
                            ></b-textarea>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" class="pt-10 pb-0">
                            <h5 class="patient-form-title">Consultant Psychiatrist</h5>
                        </v-col>
                        <v-col cols="12" class="py-0">
                            <p class="mt-5 font-weight-bold">
                                I confirm that the patient has been informed that (and has agreed to) his / her data
                                being held on file (whether in electronic or hard copy form). The patient is aware that
                                the data may be used to make decisions about their treatment.
                            </p>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="8" class="py-0">
                            <b-textarea
                                to-mutate="secure_sign_forms::patientRegistrationForm.consultantPsychiatristName"
                                :rules="validationRules.consultantPsychiatristName"
                                label="Name (please print):"
                                mandatory
                                :rows="1"
                                :auto-grow="true"
                                :printable="true"
                            ></b-textarea>
                        </v-col>
                        <v-col cols="12" sm="4" class="py-0">
                            <b-date-picker-store
                                hide-details
                                to-mutate="secure_sign_forms::patientRegistrationForm.date"
                                label="Date"
                                :rules="validationRules.date"
                                mandatory
                            ></b-date-picker-store>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" sm="8">
                            <secure-sign-form-signature-component
                                ref="signaturePad"
                                :form-type="form"
                                :is-disabled="isDisabled"
                            ></secure-sign-form-signature-component>
                        </v-col>

                        <v-col
                            v-if="!isDisabled"
                            cols="12"
                            sm="4"
                            class="d-flex flex-column justify-content-end align-items-center"
                        >
                            <secure-sign-form-recaptcha
                                ref="recaptcha"
                                :valid-recaptcha="validRecaptcha"
                                :is-disabled="isDisabled"
                                @expired="expired"
                                @verify="onRecaptchaVerify"
                            ></secure-sign-form-recaptcha>
                            <v-btn
                                :loading="loading"
                                class="mt-2"
                                max-width="304px"
                                width="100%"
                                size="x-large"
                                color="primary"
                                :disabled="isDisabled"
                                @click="submit"
                                >Submit
                            </v-btn>
                        </v-col>
                    </v-row>
                    <s-s-form-footer ref="footer">
                        <p>
                            Should you have any queries, please do not hesitate to contact the Denzapine Monitoring
                            Service Team on 0333 200 4141
                        </p>
                        <p>Job Code: UK-Denz-2300032, Version 2.0, Date of prep: Apr 2026</p>
                    </s-s-form-footer>
                </v-form>
            </v-col>
        </v-container>
    </div>
</template>

<script>
import SSFormHeader from './partials/SSFormHeader.vue';
import SSFormFooter from './partials/SSFormFooter.vue';

let formControl = require('./mixins/FormControl.js').default;
export default {
    components: { SSFormFooter, SSFormHeader },
    mixins: [formControl],
    data() {
        return {
            validationRules: {
                patientUseType: [(v) => !!v || t('users::validation.field.required')],
                proposedDateOfRegistration: [(v) => !!v || t('users::validation.field.required')],
                patientFirstName: [(v) => !!v || t('users::validation.field.required')],
                patientLastName: [(v) => !!v || t('users::validation.field.required')],
                dateOfBirth: [(v) => !!v || t('users::validation.field.required')],
                race: [(v) => !!v || t('users::validation.field.required')],
                otherRace: [(v) => !!v || t('users::validation.field.required')],
                gender: [(v) => !!v || t('users::validation.field.required')],
                history: [(v) => !!v || t('users::validation.field.required')],
                previousBrand: [(v) => !!v || t('users::validation.field.required')],
                dispensingPharmacy: [(v) => !!v || t('users::validation.field.required')],
                pharmacyAddress: [(v) => !!v || t('users::validation.field.required')],
                pharmacyPhoneNumber: [(v) => !!v || t('users::validation.field.required')],
                pharmacyEmail: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                nameOfPrescriber: [(v) => !!v || t('users::validation.field.required')],
                gmcNumber: [(v) => !!v || t('users::validation.field.required')],
                prescriberEmailAddress: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                patientType: [(v) => !!v || t('users::validation.field.required')],
                // wardAddress: [(v) => !!v || t('users::validation.field.required')],
                // wardContactName: [(v) => !!v || t('users::validation.field.required')],
                // wardTelephoneNumber: [(v) => !!v || t('users::validation.field.required')],
                // wardEmail: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                nameOfSite: [(v) => !!v || t('users::validation.field.required')],
                clinicAddress: [(v) => !!v || t('users::validation.field.required')],
                clinicContactName: [(v) => !!v || t('users::validation.field.required')],
                clinicPhoneNumber: [(v) => !!v || t('users::validation.field.required')],
                clinicEmailAddress: [(v) => !!v || t('users::validation.field.required'), this.$root.rules.email],
                neutropenia: [(v) => !!v || t('users::validation.field.required')],
                redStatus: [(v) => !!v || t('users::validation.field.required')],
                beningEthnicNeutropenia: [(v) => !!v || t('users::validation.field.required')],
                treatmentResistantSchizophrenia: [(v) => !!v || t('users::validation.field.required')],
                parkinsonDisease: [(v) => !!v || t('users::validation.field.required')],
                diagnosisDetails: [(v) => !!v || t('users::validation.field.required')],
                contraindicationsToClozapine: [(v) => !!v || t('users::validation.field.required')],
                contraindicationsToClozapineDetails: [(v) => !!v || t('users::validation.field.required')],
                boneMarrowFunction: [(v) => !!v || t('users::validation.field.required')],
                patientOutsideOfUK: [(v) => !!v || t('users::validation.field.required')],
                antipsychotics: [(v) => !!v || t('users::validation.field.required')],
                specifyAntipsychotics: [(v) => !!v || t('users::validation.field.required')],
                dateOfBaselineBloodTest: [(v) => !!v || t('users::validation.field.required')],
                whiteBloodCellCount: [(v) => !!v || t('users::validation.field.required')],
                neutrophilCount: [(v) => !!v || t('users::validation.field.required')],
                plateletCount: [(v) => !!v || t('users::validation.field.required')],
                patientBarcodeLabels: [(v) => !!v || t('users::validation.field.required')],
                otherBarcodeLabels: [(v) => !!v || t('users::validation.field.required')],
                additionalInformation: [(v) => !!v || t('users::validation.field.required')],
                consultantPsychiatristName: [(v) => !!v || t('users::validation.field.required')],
                signature: [(v) => !!v || t('users::validation.field.required')],
                date: [(v) => !!v || t('users::validation.field.required')],
            },
            radioDataBinary: [
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
            ],
            isFormValid: false,
            formType: 'patient-registration-form',
            form: 'patientRegistrationForm',
        };
    },
    computed: {
        isPatientTypeOutpatient: {
            get() {
                return this.$store.state.secure_sign_forms.patientRegistrationForm.patientType === 'Outpatient';
            },
        },
        isRaceAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.patientRegistrationForm.race === 'Other';
            },
        },
        isPreviousBrandAdditionalFieldDisabled: {
            get() {
                return (
                    this.$store.state.secure_sign_forms.patientRegistrationForm.history === 'Restart (previous brand)'
                );
            },
        },
        isParkinsonDiseaseAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.patientRegistrationForm.parkinsonDisease === 'No';
            },
        },
        isContraindicationsToClozapineAdditionalFieldDisabled: {
            get() {
                return (
                    this.$store.state.secure_sign_forms.patientRegistrationForm.contraindicationsToClozapine === 'Yes'
                );
            },
        },
        isAntipsychoticsAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.patientRegistrationForm.antipsychotics === 'Yes';
            },
        },
        isBarcodesAdditionalFieldDisabled: {
            get() {
                return this.$store.state.secure_sign_forms.patientRegistrationForm.patientBarcodeLabels === 'Other';
            },
        },
    },
    created() {
        this.getFormData();
    },
};
</script>

<style scoped></style>
